import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { GpuServer } from '../types/models/GpuServer';
import type { ApiResponseGpuServers } from '../types/api/ApiResponseGpuServer';
import { isTransient } from '../app/Datacenter/GpuServers/gpuServerStatus';
import { isAbortError, jsendErrorMessage, readJsendBody } from './gpuServerResponse';

/** List and show read the database only; statuses change at a 5-minute meter tick. */
export const GPU_SERVER_POLL_INTERVAL_MS = 30_000;

type LoadMode = 'initial' | 'refresh' | 'poll';

export interface UseGpuServersReturn {
  /** Newest first, every status (terminal ones included); `null` until the first success. */
  servers: GpuServer[] | null;
  /** Only the first load and a manual `refetch` set it — poll ticks are silent. */
  loading: boolean;
  /** The first load's or a manual refetch's failure (replaces the page with an error state). */
  error: string | null;
  /** A failed silent poll tick: the last good `servers` are kept; show at most a small warning. */
  pollError: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /datacenters/gpu-servers` — the organisation's GPU servers (docs/api-types/gpu-servers.md).
 *
 * Polls silently every 30 s while any server is `starting`, `deleting` or `unpaid`, and stops
 * otherwise (nothing changes between meter ticks). It fetches once when enabled, then only on
 * `refetch` or a poll tick: it deliberately does NOT refetch when `apiFetch` changes identity on an
 * OIDC silent renew (the latest `apiFetch` is read through a ref instead).
 */
export const useGpuServers = (enabled: boolean = true): UseGpuServersReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [servers, setServers] = useState<GpuServer[] | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hasFetchedRef = useRef(false);

  const load = useCallback(async (mode: LoadMode) => {
    // A tick never overlaps a request in flight; a manual load supersedes whatever is in flight.
    if (mode === 'poll' && abortRef.current !== null) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (mode !== 'poll') {
      setLoading(true);
      setError(null);
    }
    const fail = (message: string) => (mode === 'poll' ? setPollError(message) : setError(message));
    try {
      const response = await apiFetchRef.current('/datacenters/gpu-servers', { signal: controller.signal });
      const body = await readJsendBody<ApiResponseGpuServers>(response);
      if (controller.signal.aborted) {
        return;
      }
      if (body?.status === 'success' && Array.isArray(body.data?.servers)) {
        setServers(body.data.servers);
        setError(null);
        setPollError(null);
        return;
      }
      fail(jsendErrorMessage(body, 'Could not load your GPU servers.'));
    } catch (err) {
      if (isAbortError(err, controller.signal)) {
        return;
      }
      fail(err instanceof Error ? err.message : 'An error occurred while loading your GPU servers.');
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        if (mode !== 'poll') {
          setLoading(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void load('initial');
    }
  }, [enabled, auth.isAuthenticated, load]);

  const shouldPoll = enabled && servers !== null && servers.some((server) => isTransient(server.status));
  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }
    const handle = window.setInterval(() => {
      void load('poll');
    }, GPU_SERVER_POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [shouldPoll, load]);

  // Forgetting the fetch makes a remount read again — StrictMode's development remount included,
  // whose cleanup aborts the first read (without the reset, nothing would ever re-issue it).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      hasFetchedRef.current = false;
    },
    [],
  );

  const refetch = useCallback(() => load('refresh'), [load]);

  return { servers, loading, error, pollError, refetch };
};

export default useGpuServers;
